/**
 * Vendor Badge Component
 * Displays vendor logo, name, and verification status
 */

import { cn } from "@utils/cn";
import type { EmbeddedVendor } from "@/types/tools";

interface VendorBadgeProps {
  vendor: EmbeddedVendor;
  size?: "sm" | "md" | "lg";
  showName?: boolean;
  className?: string;
}

// Verified checkmark icon
function VerifiedIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-label="Verified vendor"
    >
      <path
        fillRule="evenodd"
        d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  );
}

// Default vendor avatar when no logo
function DefaultVendorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 4c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm0 14c-2.03 0-4.43-.82-6.14-2.88C7.55 15.8 9.68 15 12 15s4.45.8 6.14 2.12C16.43 19.18 14.03 20 12 20z" />
    </svg>
  );
}

export function VendorBadge({
  vendor,
  size = "sm",
  showName = true,
  className,
}: VendorBadgeProps) {
  const sizeClasses = {
    sm: {
      container: "gap-1.5",
      logo: "w-5 h-5",
      name: "text-xs",
      verified: "w-3.5 h-3.5",
    },
    md: {
      container: "gap-2",
      logo: "w-6 h-6",
      name: "text-sm",
      verified: "w-4 h-4",
    },
    lg: {
      container: "gap-2.5",
      logo: "w-8 h-8",
      name: "text-base",
      verified: "w-5 h-5",
    },
  };

  const sizes = sizeClasses[size];

  return (
    <div className={cn("inline-flex items-center", sizes.container, className)}>
      {/* Logo */}
      {vendor.logoUrl ? (
        <img
          src={vendor.logoUrl}
          alt={`${vendor.name} logo`}
          className={cn(
            sizes.logo,
            "rounded-full object-cover bg-gray-100 dark:bg-gray-700",
          )}
        />
      ) : (
        <span
          className={cn(
            sizes.logo,
            "rounded-full flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400",
          )}
        >
          <DefaultVendorIcon className="w-3/4 h-3/4" />
        </span>
      )}

      {/* Name + Verified */}
      {showName && (
        <span className="inline-flex items-center gap-1">
          <span
            className={cn(
              sizes.name,
              "font-medium text-gray-700 dark:text-gray-300",
            )}
          >
            {vendor.name}
          </span>
          {vendor.isVerified && (
            <VerifiedIcon
              className={cn(sizes.verified, "text-blue-500 flex-shrink-0")}
            />
          )}
        </span>
      )}
    </div>
  );
}

/**
 * Compact vendor display for cards
 */
export function VendorLabel({
  vendor,
  className,
}: {
  vendor: EmbeddedVendor;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400",
        className,
      )}
    >
      <span className="truncate">{vendor.name}</span>
      {vendor.isVerified && (
        <VerifiedIcon className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
      )}
    </span>
  );
}
